import { useEffect, useRef, useState } from 'react'
import { loadLevelById } from '../data/loadLevel'
import { RadarScene } from '../render/RadarScene'
import { setAltitude, setHeading, setSpeed } from '../game/CommandService'
import { etaSecondsTo, resolveAircraftTarget } from '../game/aircraftTarget'
import { performanceFor } from '../game/AircraftDatabase'
import { useReadbackStore } from '../state/readbackStore'
import { useUiStore, type AircraftSummary } from '../state/store'
import { CommandPanel } from './CommandPanel'
import { FeedbackModal } from './FeedbackModal'
import { HelpModal } from './HelpModal'
import { TutorialGuide, hasSeenTutorial } from './TutorialGuide'
import { EndScreen } from './EndScreen'
import { ScoreHud } from './ScoreHud'
import { PositionSelect } from './PositionSelect'
import { persistentArrivalsEnabled } from '../state/store'
import { ReadbackToasts } from './ReadbackToasts'
import { FlightStrip, type FlightStripRow } from './FlightStrip'
import { useProfileStore } from '../state/profileStore'
import { setActivePresenceLevel } from '../data/presence'
import { recordRunIfAnonymous } from '../data/anonymousNudge'
import { logLevelStart, logLevelEnd } from '../analytics/AnalyticsManager'
import { findLevel } from '../data/loadLevel'
import type { Airport, LevelGoalsSummary, Waypoint } from '../types/LevelData'
import { describeGoal } from '../types/LevelData'

function briefSeenKey(levelId: string): string {
  return `flatout.web.briefSeen.${levelId}`
}

/// Pick up runtime overrides from the URL: `?difficulty=Master`
/// swaps the difficulty preset, `?rate=120` forces a specific
/// rate-of-aircraft-per-hour (handy for fast-iterating tests).
function readSessionOptions(): { difficultyName?: string; rateOverride?: number } {
  const params = new URLSearchParams(window.location.search)
  const difficulty = params.get('difficulty') ?? undefined
  const rateStr = params.get('rate')
  const rate = rateStr ? Number(rateStr) : NaN
  return {
    difficultyName: difficulty,
    rateOverride: Number.isFinite(rate) ? rate : undefined,
  }
}

/// Hosts the Pixi radar + the React chrome (command panel, level
/// brief, end screen). The `mountKey` state is used to re-mount the
/// entire subtree on Restart, which gives a clean reset without us
/// having to re-implement teardown logic for every layer.
export function GameView({
  levelId,
  onHome,
}: {
  levelId: string
  onHome: () => void
}) {
  const [mountKey, setMountKey] = useState(0)
  return (
    <GameViewInner
      key={`${levelId}:${mountKey}`}
      levelId={levelId}
      onRestart={() => setMountKey((k) => k + 1)}
      onHome={onHome}
    />
  )
}

function GameViewInner({
  levelId,
  onRestart,
  onHome,
}: {
  levelId: string
  onRestart: () => void
  onHome: () => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<RadarScene | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [feedbackOpen, setFeedbackOpen] = useState<boolean>(false)
  const [briefText, setBriefText] = useState<import('../types/LevelData').LocalizedString | undefined>(undefined)
  const [briefGoals, setBriefGoals] = useState<LevelGoalsSummary | null>(null)
  const [airports, setAirports] = useState<Airport[]>([])
  const [waypoints, setWaypoints] = useState<Waypoint[]>([])
  const [timeMultiplier, setTimeMultiplier] = useState<number>(1)
  /// Tutorial-guide visibility for the TUTORIAL level. Initialised
  /// from localStorage so a player who completed it once never
  /// sees it again. The component itself sets the localStorage
  /// flag on the final-step completion or when the player taps
  /// Skip; this state just hides the overlay for the current
  /// session so it doesn't flash back on a re-render.
  const [tutorialDismissed, setTutorialDismissed] = useState<boolean>(() =>
    hasSeenTutorial(),
  )
  /// Tracks whether we've already persisted this level-end to the
  /// profile so the recordLevelEnd Firestore write fires exactly once
  /// per session (not on every onTick after the level finishes).
  const recordedEndRef = useRef(false)
  // Aircraft already offered a pinned arrival row (prevents re-pin
  // after dismissal). Reset per level mount with the scene.
  const seenArrivalsRef = useRef<Set<string>>(new Set())

  const ui = useUiStore()
  const setLastViewedLevel = useProfileStore((s) => s.setLastViewedLevel)
  const recordLevelEnd = useProfileStore((s) => s.recordLevelEnd)

  useEffect(() => {
    // Record this level as the user's last-viewed for the home
    // screen highlight. Fire-and-forget; failure here doesn't block
    // gameplay (e.g., offline / rules misconfigured).
    void setLastViewedLevel(levelId)
  }, [levelId, setLastViewedLevel])

  // Stamp the user's RTDB presence entry with this levelId so the
  // home-screen breakdown and per-pin badges can count "x in Lukla
  // now". Clears on unmount (back to home / sign-out / route-away).
  // We need a UID; profileStore's `state.kind` becomes 'ready' once
  // ensureSignedIn lands, and GameView is only mounted after that.
  useEffect(() => {
    const ps = useProfileStore.getState()
    const uid = ps.state.kind === 'ready' ? ps.state.user.uid : null
    if (!uid) return
    setActivePresenceLevel(uid, levelId)
    return () => {
      setActivePresenceLevel(uid, null)
    }
  }, [levelId])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Fresh slate per level mount — toasts from a previous session
    // (or the in-flight ones at Restart) shouldn't leak across.
    useReadbackStore.getState().clearAll()

    let scene: RadarScene | null = null
    let cancelled = false

    const init = async () => {
      try {
        const bundle = await loadLevelById(levelId)
        if (cancelled) return

        scene = await RadarScene.mount(
          container,
          bundle,
          {
            onSelect: (id) => {
              if (!scene) return
              scene.session.select(id)
              const ui = useUiStore.getState()
              ui.setSelected(id, id ? summarize(scene, id) : null)
              // Any click on the canvas (aircraft or empty map)
              // dismisses the strip — strip and command panel are
              // mutually exclusive (the panel render is gated on
              // `!flightStripVisible`), so closing the strip is
              // what surfaces the selection window.
              if (ui.flightStripVisible) ui.setFlightStripVisible(false)
            },
            onReadback: (text) => useReadbackStore.getState().push(text),
            onTick: () => {
              if (!scene) return
              const state = useUiStore.getState()
              if (state.selectedId) {
                state.setSelectedSummary(summarize(scene, state.selectedId))
              }
              if (scene.session.endState.kind !== state.endState.kind) {
                state.setEndState(scene.session.endState)
                // Persist the outcome on the first transition into
                // a finished state — guarded so re-renders don't
                // double-write.
                if (
                  scene.session.endState.kind === 'finished' &&
                  !recordedEndRef.current
                ) {
                  recordedEndRef.current = true
                  void recordLevelEnd({
                    levelId,
                    difficulty: scene.session.difficultyName,
                    succeeded: scene.session.endState.success,
                    score: scene.session.totalScore,
                    successCount: scene.session.successCount,
                    totalHandled:
                      scene.session.successCount + scene.session.failCount,
                  })
                  logLevelEnd({
                    levelId,
                    difficulty: scene.session.difficultyName,
                    success: scene.session.endState.success,
                    finalScore: scene.session.totalScore,
                    successCount: scene.session.successCount,
                    totalHandled:
                      scene.session.successCount + scene.session.failCount,
                  })
                  // Soft-trial nudge: bump the anonymous run counter
                  // so the home screen can prompt sign-in after the
                  // threshold is hit. No-op for signed-in users.
                  {
                    const profileState = useProfileStore.getState().state
                    const sessionUser =
                      profileState.kind === 'ready' ? profileState.user : null
                    recordRunIfAnonymous(sessionUser)
                  }
                }
              }
              // Persistent arrival rows (dev-board feedback, Dandop):
              // pin a row for each new airborne arrival/emergency;
              // prune rows whose aircraft left the sim. Selection
              // dismissal lives in useUiStore.setSelected.
              {
                const liveIds = new Set<string>()
                for (const a of scene.session.aircrafts) {
                  liveIds.add(a.id)
                  if (seenArrivalsRef.current.has(a.id)) continue
                  const arrivalAirport =
                    a.goal.land?.airportName ?? a.goal.landHelipad?.airportName
                  if ((arrivalAirport || a.emergency) && a.status !== 'awaitingTakeoff') {
                    seenArrivalsRef.current.add(a.id)
                    if (persistentArrivalsEnabled()) {
                      state.pushPendingArrival({
                        id: a.id,
                        flightNumber: a.flightNumber,
                        detail: a.emergency
                          ? String(a.emergency.reason)
                          : (arrivalAirport ?? ''),
                        isEmergency: !!a.emergency,
                      })
                    }
                  }
                }
                state.prunePendingArrivals(liveIds)
              }
              const session = scene.session
              const cur = state.score
              if (
                cur.total !== session.totalScore ||
                cur.successCount !== session.successCount ||
                cur.failCount !== session.failCount ||
                cur.expectedTotal !== session.expectedTotal ||
                cur.targetCompleted !== session.goals.targetCompleted ||
                cur.separationInfringements !== session.separationInfringements
              ) {
                state.setScore({
                  total: session.totalScore,
                  recent: session.recentScoreEvents(),
                  successCount: session.successCount,
                  failCount: session.failCount,
                  expectedTotal: session.expectedTotal,
                  targetCompleted: session.goals.targetCompleted,
                  breakdown: session.breakdown,
                  separationInfringements: session.separationInfringements,
                })
              }
              // Live shift goal progress (positioned shifts only).
              if (session.isLive) {
                const sh = state.shift
                if (
                  sh.aircraftWorked !== session.aircraftWorked ||
                  sh.goal !== session.shiftGoal
                ) {
                  state.setShift({
                    aircraftWorked: session.aircraftWorked,
                    goal: session.shiftGoal,
                  })
                }
              }
              // Live wind — round before comparing so 0.3°/sec drift
              // doesn't fire a setWind on every tick (most ticks the
              // rounded value hasn't changed).
              const windDir = Math.round(session.effectiveWindDirection)
              const windKts = Math.round(session.windSpeedKts)
              if (
                state.wind.directionDeg !== windDir ||
                state.wind.speedKts !== windKts
              ) {
                state.setWind({ directionDeg: windDir, speedKts: windKts })
              }
            },
          },
          readSessionOptions(),
        )
        if (cancelled) {
          scene.destroy()
          scene = null
          return
        }
        sceneRef.current = scene
        setBriefText(bundle.level.levelBrief)
        setBriefGoals(scene.session.goals)
        setAirports(bundle.level.airports)
        // Synthesise a Waypoint per helipad so the Direct-To picker
        // exposes them — otherwise helicopters with a `landHelipad`
        // goal can only be vectored by dead reckoning. Pad ID is
        // used as the display name (e.g. "HMC", "PAD-A").
        const helipadWaypoints = bundle.level.airports.flatMap((ap) =>
          (ap.helipads ?? []).map((pad) => ({
            name: pad.id,
            type: 'helipad' as const,
            coordinate: pad.coordinate,
          })),
        )
        setWaypoints([...bundle.level.waypoints, ...helipadWaypoints])
        // Show level brief on first ever entry — gated per-level so
        // each level's brief shows once independently.
        if (bundle.level.levelBrief && !localStorage.getItem(briefSeenKey(levelId))) {
          useUiStore.getState().setBriefVisible(true)
        }
        // Analytics: level start fires once per mount. mountKey
        // bumps on Restart, so a fresh attempt counts as a fresh
        // start.
        const registryEntry = findLevel(levelId)
        logLevelStart({
          levelId,
          difficulty: scene.session.difficultyName,
          isFree: registryEntry?.isFree ?? true,
        })
      } catch (e) {
        console.error('GameView load failed', e)
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      }
    }
    void init()

    return () => {
      cancelled = true
      sceneRef.current = null
      if (scene) {
        scene.destroy()
        scene = null
      }
    }
  }, [])

  const pushReadback = (text: string) => useReadbackStore.getState().push(text)

  const handlers = {
    onSetHeading: (h: number, direction: 'shortest' | 'left' | 'right' = 'shortest') => {
      const scene = sceneRef.current
      const id = useUiStore.getState().selectedId
      if (!scene || !id) return
      const a = scene.session.aircrafts.find((x) => x.id === id)
      if (!a) return
      const before = a.headingDeg
      setHeading(a, h, direction)
      // Readback honors the *commanded* direction when forced;
      // otherwise we report the natural shortest-arc side.
      const turn =
        direction === 'left'
          ? 'left'
          : direction === 'right'
            ? 'right'
            : turnDirection(before, h)
      const hh = String(Math.round(((h % 360) + 360) % 360)).padStart(3, '0')
      pushReadback(
        turn === 'none'
          ? `${a.flightNumber}, heading ${hh}`
          : `${a.flightNumber}, turn ${turn} heading ${hh}`,
      )
    },
    onSetAltitude: (alt: number) => {
      const scene = sceneRef.current
      const id = useUiStore.getState().selectedId
      if (!scene || !id) return null
      const a = scene.session.aircrafts.find((x) => x.id === id)
      if (!a) return null
      const before = a.altitudeFt
      const result = setAltitude(a, alt)
      if (result.clamped) {
        pushReadback(
          `${a.flightNumber}, unable — ${result.limit === 'min' ? 'minimum' : 'maximum'} altitude ${result.commanded.toLocaleString()} ft`,
        )
      } else {
        const verb =
          result.commanded > before + 50
            ? 'climb and maintain'
            : result.commanded < before - 50
              ? 'descend and maintain'
              : 'maintain'
        pushReadback(`${a.flightNumber}, ${verb} ${result.commanded.toLocaleString()}`)
      }
      return result
    },
    onSetSpeed: (kts: number) => {
      const scene = sceneRef.current
      const id = useUiStore.getState().selectedId
      if (!scene || !id) return null
      const a = scene.session.aircrafts.find((x) => x.id === id)
      if (!a) return null
      const before = a.targetSpeedKts
      const result = setSpeed(a, kts)
      if (result.clamped) {
        pushReadback(
          `${a.flightNumber}, unable — ${result.limit === 'min' ? 'minimum' : 'maximum'} ${result.commanded} kt`,
        )
      } else {
        const verb =
          result.commanded > before
            ? 'increase speed to'
            : result.commanded < before
              ? 'reduce speed to'
              : 'maintain speed'
        pushReadback(
          verb === 'maintain speed'
            ? `${a.flightNumber}, maintain ${result.commanded} kt`
            : `${a.flightNumber}, ${verb} ${result.commanded} kt`,
        )
      }
      return result
    },
    onClearedForApproach: () => {
      const scene = sceneRef.current
      const id = useUiStore.getState().selectedId
      if (!scene || !id) return
      const a = scene.session.aircrafts.find((x) => x.id === id)
      const callsign = a?.flightNumber ?? ''
      const result = scene.commandClearedForApproach(id)
      if (!result.ok) {
        pushReadback(`${callsign}, unable — ${approachReasonText(result.reason)}`)
      } else {
        pushReadback(`${callsign}, cleared for approach`)
      }
    },
    onClearToLand: () => {
      const scene = sceneRef.current
      const id = useUiStore.getState().selectedId
      if (!scene || !id) return
      const a = scene.session.aircrafts.find((x) => x.id === id)
      const callsign = a?.flightNumber ?? ''
      const result = scene.clearToLand(id)
      if (!result.ok) {
        pushReadback(`${callsign}, unable — ${landingReasonText(result.reason)}`)
      } else {
        pushReadback(`${callsign}, cleared to land`)
      }
    },
    onTakeoff: () => {
      const scene = sceneRef.current
      const id = useUiStore.getState().selectedId
      if (!scene || !id) return
      const a = scene.session.aircrafts.find((x) => x.id === id)
      const callsign = a?.flightNumber ?? ''
      const result = scene.commandTakeoff(id)
      if (!result.ok) {
        pushReadback(`${callsign}, unable — ${takeoffReasonText(result.reason)}`)
      } else {
        pushReadback(`${callsign}, cleared for takeoff`)
      }
    },
    onGoAround: () => {
      const scene = sceneRef.current
      const id = useUiStore.getState().selectedId
      if (!scene || !id) return
      const a = scene.session.aircrafts.find((x) => x.id === id)
      const callsign = a?.flightNumber ?? ''
      const result = scene.commandGoAround(id)
      if (!result.ok) {
        pushReadback(`${callsign}, unable — not on approach`)
      } else {
        pushReadback(`${callsign}, going around`)
      }
    },
    onDirectTo: (waypointName: string | null) => {
      const scene = sceneRef.current
      const id = useUiStore.getState().selectedId
      if (!scene || !id) return
      const a = scene.session.aircrafts.find((x) => x.id === id)
      const callsign = a?.flightNumber ?? ''
      const result = scene.commandDirectTo(id, waypointName)
      if (!result.ok) {
        pushReadback(`${callsign}, unable — ${directReasonText(result.reason)}`)
      } else if (waypointName) {
        pushReadback(`${callsign}, direct ${waypointName}`)
      } else {
        pushReadback(`${callsign}, cancel direct, own navigation`)
      }
    },
    onHold: (waypointName: string) => {
      const scene = sceneRef.current
      const id = useUiStore.getState().selectedId
      if (!scene || !id) return
      const a = scene.session.aircrafts.find((x) => x.id === id)
      const callsign = a?.flightNumber ?? ''
      const result = scene.commandHold(id, waypointName)
      if (!result.ok) {
        pushReadback(`${callsign}, unable — ${directReasonText(result.reason)}`)
        return
      }
      if (result.goAround) {
        pushReadback(`${callsign}, going around, hold at ${waypointName}`)
      } else {
        pushReadback(`${callsign}, hold at ${waypointName}`)
      }
    },
    onCancelHold: () => {
      const scene = sceneRef.current
      const id = useUiStore.getState().selectedId
      if (!scene || !id) return
      const a = scene.session.aircrafts.find((x) => x.id === id)
      const callsign = a?.flightNumber ?? ''
      const result = scene.commandCancelHold(id)
      if (result.ok) {
        pushReadback(`${callsign}, cancel hold, own navigation`)
      }
    },
    onDeselect: () => {
      const scene = sceneRef.current
      if (!scene) return
      scene.session.select(null)
      useUiStore.getState().setSelected(null, null)
    },
    onHandoff: () => {
      const scene = sceneRef.current
      const id = useUiStore.getState().selectedId
      if (!scene || !id) return
      const callsign = scene.session.aircrafts.find((x) => x.id === id)?.flightNumber ?? ''
      if (scene.session.handOffAircraft(id)) {
        pushReadback(`${callsign}, contact Center, good day`)
        useUiStore.getState().setSelected(null, null)
      }
    },
  }

  const dismissBrief = () => {
    localStorage.setItem(briefSeenKey(levelId), '1')
    useUiStore.getState().setBriefVisible(false)
  }

  /// Polled by the FlightStrip every 250ms. Builds a fresh row list
  /// from live session state — the strip doesn't keep its own copy.
  const getAllStripRows = (): FlightStripRow[] => {
    const scene = sceneRef.current
    if (!scene) return []
    return scene.session.aircrafts.map((a) => {
      const summary = summarize(scene, a.id)
      // summarize never returns null for an existing aircraft.
      if (!summary) {
        throw new Error(`summarize returned null for active aircraft ${a.id}`)
      }
      return { ...summary, goal: a.goal }
    })
  }

  const onSelectFromStrip = (id: string) => {
    const scene = sceneRef.current
    if (!scene) return
    scene.panToAircraft(id)
    scene.session.select(id)
    const ui = useUiStore.getState()
    ui.setSelected(id, summarize(scene, id))
    // Picking a row from the strip is a "go work this plane" gesture
    // — close the strip so the CommandPanel takes over (panel is
    // gated on `!flightStripVisible`).
    ui.setFlightStripVisible(false)
  }

  return (
    <>
      <div ref={containerRef} className="absolute inset-0" />
      {error && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md bg-red-900/80 px-4 py-3 text-sm text-white shadow-lg">
          Failed to load level: {error}
        </div>
      )}
      <ScoreHud
        score={ui.score}
        selected={ui.selected}
        wind={ui.wind}
        shift={ui.shift}
        isLive={!!sceneRef.current?.session.isLive}
        pendingArrivals={ui.pendingArrivals}
        onPickArrival={(id) => {
          const scene = sceneRef.current
          if (!scene) return
          scene.session.select(id)
          useUiStore.getState().setSelected(id, summarize(scene, id))
        }}
      />
      <ReadbackToasts />
      {ui.briefVisible && (
        <HelpModal
          brief={briefText}
          goals={briefGoals}
          initialTab="brief"
          onDismiss={dismissBrief}
        />
      )}
      {levelId === 'TUTORIAL' && !tutorialDismissed && (
        <TutorialGuide
          selectedId={ui.selectedId}
          aircraft={ui.selected}
          onSkip={() => setTutorialDismissed(true)}
        />
      )}
      {ui.flightStripVisible && (
        <FlightStrip
          getAll={getAllStripRows}
          selectedId={ui.selectedId}
          onSelect={onSelectFromStrip}
          onClose={() => useUiStore.getState().setFlightStripVisible(false)}
        />
      )}
      {ui.selected && !ui.flightStripVisible && airports.length > 0 && (
        <CommandPanel
          aircraft={ui.selected}
          waypoints={waypoints}
          handlers={handlers}
          isLive={!!sceneRef.current?.session.isLive}
        />
      )}
      {ui.endState.kind === 'preShift' && sceneRef.current && (
        <PositionSelect
          positions={sceneRef.current.session.shiftPositions}
          presets={sceneRef.current.session.shiftGoalPresets}
          onStart={(positionId, goal) => {
            const scene = sceneRef.current
            if (!scene) return
            scene.session.startShift(positionId, goal)
            useUiStore.getState().setEndState(scene.session.endState)
          }}
        />
      )}
      {ui.endState.kind === 'finished' && (
        <EndScreen
          success={ui.endState.success}
          score={ui.score}
          live={
            sceneRef.current?.session.isLive
              ? {
                  worked: ui.shift.aircraftWorked,
                  goal: ui.shift.goal,
                  handoffs: sceneRef.current.session.handoffCount,
                  unhandledExits: sceneRef.current.session.unhandledExitCount,
                }
              : null
          }
          onRestart={onRestart}
          onHome={onHome}
        />
      )}
      {/* Top-right toolbar.
          Desktop (md+): everything inline — Home / 1×–8× / Strip / ?.
          Phone: tighter padding + smaller height; Strip and the 8×
          slot drop out (Strip is unusable on phone width; 8× is
          rarely chosen and removing it keeps the multiplier bar to
          three taps wide). */}
      <div className="pointer-events-auto absolute right-2 top-2 z-10 flex items-center gap-1 md:right-4 md:top-3 md:gap-2">
        <button
          onClick={onHome}
          title="Back to level select"
          className="h-7 rounded-md bg-slate-900/70 px-2 text-xs font-semibold text-white/80 shadow ring-1 ring-white/10 hover:bg-slate-800/80 md:h-8 md:px-3"
        >
          ← Home
        </button>
        <button
          onClick={() => sceneRef.current?.panToHome()}
          title="Recenter on the home airport"
          className="h-7 rounded-md bg-slate-900/70 px-2 text-xs font-semibold text-white/80 shadow ring-1 ring-white/10 hover:bg-slate-800/80 md:h-8 md:px-3"
        >
          ⌖ Location
        </button>
        <div className="flex h-7 overflow-hidden rounded-md bg-slate-900/70 text-xs font-semibold shadow ring-1 ring-white/10 md:h-8">
          {[1, 2, 4, 8].map((m) => (
            <button
              key={m}
              onClick={() => {
                sceneRef.current?.setTimeMultiplier(m)
                setTimeMultiplier(m)
              }}
              title={`${m}× simulation speed`}
              className={
                'px-1.5 transition-colors md:px-2 ' +
                (m === 8 ? 'hidden md:block ' : '') +
                (timeMultiplier === m
                  ? 'bg-emerald-700/90 text-white'
                  : 'text-white/70 hover:bg-slate-800/80')
              }
            >
              {m}×
            </button>
          ))}
        </div>
        <button
          onClick={() =>
            useUiStore.getState().setFlightStripVisible(!useUiStore.getState().flightStripVisible)
          }
          title="Flight strip"
          className={
            'h-7 rounded-md px-2 text-xs font-semibold shadow ring-1 ring-white/10 md:h-8 md:px-3 ' +
            (ui.flightStripVisible
              ? 'bg-emerald-700/80 text-white'
              : 'bg-slate-900/70 text-white/80 hover:bg-slate-800/80')
          }
        >
          Strip
        </button>
        <button
          onClick={() => setFeedbackOpen(true)}
          title="Send feedback"
          className="h-7 rounded-md bg-slate-900/70 px-2 text-xs font-semibold text-white/80 shadow ring-1 ring-white/10 hover:bg-slate-800/80 md:h-8 md:px-3"
        >
          Feedback
        </button>
        <button
          onClick={() => useUiStore.getState().setBriefVisible(true)}
          title="Brief"
          className="h-7 w-7 rounded-full bg-slate-900/70 text-xs font-bold text-white/80 shadow ring-1 ring-white/10 hover:bg-slate-800/80 md:h-8 md:w-8 md:text-sm"
        >
          ?
        </button>
      </div>
      {feedbackOpen && (
        <FeedbackModal levelId={levelId} onDismiss={() => setFeedbackOpen(false)} />
      )}
    </>
  )
}

/// Shorter side of the heading delta for "turn left" / "turn right".
function turnDirection(fromDeg: number, toDeg: number): 'left' | 'right' | 'none' {
  let diff = ((toDeg - fromDeg + 540) % 360) - 180
  if (Math.abs(diff) < 1) return 'none'
  return diff > 0 ? 'right' : 'left'
}

function landingReasonText(r: string | undefined): string {
  switch (r) {
    case 'not_aligned':
      return 'turn to runway'
    case 'off_centerline':
      return 'off centerline'
    case 'too_far':
      return 'too far'
    case 'descent_too_steep':
      return 'too high'
    case 'speed_zero':
      return 'no airspeed'
    case 'invalid_runway':
      return 'no runway'
    default:
      return 'unable'
  }
}

function approachReasonText(r: string | undefined): string {
  switch (r) {
    case 'not_aligned':
      return 'turn closer to runway'
    case 'too_far':
      return 'closer to airport first'
    case 'speed_zero':
      return 'no airspeed'
    case 'already_landing':
      return 'already on approach'
    case 'invalid_runway':
      return 'no runway'
    case 'past_threshold':
      return 'past threshold, vector around'
    default:
      return 'unable'
  }
}

function takeoffReasonText(r: string | undefined): string {
  switch (r) {
    case 'not_on_ground':
      return 'not on ground'
    case 'no_target_waypoint':
      return 'no departure fix'
    case 'thunderstorm':
      return 'thunderstorm over the runway'
    default:
      return 'unable'
  }
}

function directReasonText(r: string | undefined): string {
  switch (r) {
    case 'unknown_waypoint':
      return 'unknown waypoint'
    case 'on_ground':
      return 'on the ground'
    default:
      return 'unable'
  }
}

/// Build the React-side summary from live aircraft state. The
/// landing-clearance evaluator decides whether the button is enabled
/// AND surfaces the reason when it isn't, so the player can see what
/// they need to fix (alignment, distance, descent rate, centerline).
function summarize(scene: RadarScene, id: string): AircraftSummary | null {
  const a = scene.session.aircrafts.find((x) => x.id === id)
  if (!a) return null
  const inAir =
    a.status !== 'landing' && a.status !== 'landed' && a.status !== 'completed'
  const evalResult = inAir ? scene.evaluateClearToLand(id) : null
  // Approach evaluator only relevant for in-air aircraft that aren't
  // already on the localizer-intercept (button is for engaging it).
  const approachEval =
    inAir && !a.approachIntercept ? scene.evaluateClearedForApproach(id) : null
  const perf = performanceFor(a.type)

  // Distance + ETA to wherever the aircraft is heading. Skipped on
  // the ground (awaitingTakeoff has no useful target) and after
  // touchdown (landed/completed) — those aircraft don't need the
  // HUD line.
  let targetName: string | null = null
  let distanceToTargetNM: number | null = null
  let etaSec: number | null = null
  const wantTarget =
    a.status !== 'awaitingTakeoff' &&
    a.status !== 'landed' &&
    a.status !== 'completed'
  if (wantTarget) {
    const target = resolveAircraftTarget(a, scene.session.bundle.level, scene.projector)
    if (target) {
      targetName = target.name
      const result = etaSecondsTo(a, target.point, scene.session.pxPerNM)
      distanceToTargetNM = result.distanceNM
      etaSec = result.etaSec
    }
  }

  return {
    id: a.id,
    flightNumber: a.flightNumber,
    headingDeg: a.headingDeg,
    targetHeadingDeg: a.targetHeadingDeg,
    altitudeFt: a.altitudeFt,
    targetAltitudeFt: a.targetAltitudeFt,
    speedKts: a.speedKts,
    targetSpeedKts: a.targetSpeedKts,
    minSpeedKts: perf.minSpeedKts,
    maxSpeedKts: perf.maxSpeedKts,
    type: a.type,
    status: a.status,
    canClearToLand: evalResult?.ok ?? false,
    clearToLandRejection: evalResult && !evalResult.ok ? evalResult.reason ?? null : null,
    awaitingTakeoff: a.status === 'awaitingTakeoff',
    departureRunway: a.departureRunway,
    directToName: a.directToName,
    canClearForApproach: approachEval?.ok ?? false,
    inApproachIntercept: a.approachIntercept != null,
    hold: a.holdingState
      ? { fixName: a.holdingState.fixName, phase: a.holdingState.phase }
      : null,
    emergency: a.emergency
      ? { reason: a.emergency.reason, countdownSec: a.emergency.countdownSec }
      : null,
    canHandoff: scene.session.canHandOff(a),
    goalDescription: describeGoal(a.goal),
    collisionStatus: a.collisionStatus,
    collisionDetail: a.collisionDetail,
    targetName,
    distanceToTargetNM,
    etaSec,
    squawk: a.squawk,
  }
}
